import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { FileText, Calendar, Users } from 'lucide-react';
import { noteSpaceService, NoteSpace } from '@/services/supabase';
import { useToast } from '@/components/ui/use-toast';
import { getNotesFromInterviews } from '@/services/analysis';

interface InterviewSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedInterviews: NoteSpace[], analysisName: string) => void;
}

export const InterviewSelectionModal: React.FC<InterviewSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const [noteSpaces, setNoteSpaces] = useState<NoteSpace[]>([]);
  const [selectedSpaces, setSelectedSpaces] = useState<Set<string>>(new Set());
  const [analysisName, setAnalysisName] = useState('Multi-Interview Analysis');
  const [loading, setLoading] = useState(false);
  const [previewCounts, setPreviewCounts] = useState<Record<string, number>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadNoteSpaces();
    }
  }, [isOpen]);

  const loadNoteSpaces = async () => {
    try {
      setLoading(true);
      const spaces = await noteSpaceService.getAll();
      setNoteSpaces(spaces);
      
      // Get note counts for each space
      const counts: Record<string, number> = {};
      for (const space of spaces) {
        const notes = await getNotesFromInterviews([space.id]);
        counts[space.id] = notes.length;
      }
      setPreviewCounts(counts);
    } catch (error) {
      console.error('Error loading note spaces:', error);
      toast({
        title: "Error",
        description: "Failed to load your interviews. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSpaceToggle = (spaceId: string) => {
    const newSelected = new Set(selectedSpaces);
    if (newSelected.has(spaceId)) {
      newSelected.delete(spaceId);
    } else {
      newSelected.add(spaceId);
    }
    setSelectedSpaces(newSelected);
  };

  const handleConfirm = () => {
    if (selectedSpaces.size < 2) {
      toast({
        title: "Selection Required",
        description: "Please select at least 2 interviews to analyze.",
        variant: "destructive",
      });
      return;
    }

    const selectedNoteSpaces = noteSpaces.filter(space => selectedSpaces.has(space.id));
    onConfirm(selectedNoteSpaces, analysisName.trim() || 'Multi-Interview Analysis');
    handleClose();
  };

  const handleClose = () => {
    setSelectedSpaces(new Set());
    setAnalysisName('Multi-Interview Analysis');
    onClose();
  };

  const totalSelectedNotes = Array.from(selectedSpaces).reduce((total, spaceId) => {
    return total + (previewCounts[spaceId] || 0);
  }, 0);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Analyze Across Interviews
          </DialogTitle>
          <DialogDescription>
            Select interviews to analyze together and discover patterns across multiple sessions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div>
            <Label htmlFor="analysis-name">Analysis Name</Label>
            <Input
              id="analysis-name"
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              placeholder="Enter a name for this analysis"
              className="mt-1"
            />
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <Label>Select Interviews ({selectedSpaces.size} selected)</Label>
              {selectedSpaces.size > 0 && (
                <Badge variant="secondary">
                  {totalSelectedNotes} notes total
                </Badge>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                {noteSpaces.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No interviews found. Create some interviews first.</p>
                  </div>
                ) : (
                  noteSpaces.map((space) => (
                    <Card
                      key={space.id}
                      className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedSpaces.has(space.id) ? 'ring-2 ring-amber-500 bg-amber-50' : ''
                      }`}
                      onClick={() => handleSpaceToggle(space.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={selectedSpaces.has(space.id)}
                          onChange={() => handleSpaceToggle(space.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="font-medium truncate">{space.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(space.created_at).toLocaleDateString()}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {previewCounts[space.id] || 0} notes
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={selectedSpaces.size < 2 || loading}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          >
            Start Analysis ({selectedSpaces.size} interviews)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};